import { useState, useRef, useMemo } from "react";
import { audioSrc } from "@/components/FamilyVoices";
import { Play, Pause, Music, Headphones, HelpCircle, Check } from "lucide-react";
import { GardienBadge } from "@/components/GardienBadge";

const shuffle = (a) => [...a].sort(() => Math.random() - 0.5);

function BigPlay({ url, testId, label = "Écouter" }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const toggle = () => {
    if (playing) { ref.current.pause(); setPlaying(false); return; }
    document.querySelectorAll("audio").forEach((a) => a !== ref.current && a.pause());
    ref.current.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  };
  return (
    <>
      <audio ref={ref} src={audioSrc(url)} preload="none" onEnded={() => setPlaying(false)} onPause={() => setPlaying(false)} />
      <button onClick={toggle} data-testid={testId}
        className="inline-flex items-center gap-3 h-20 px-8 rounded-full bg-emerald-700 text-white font-patient font-black text-2xl active:scale-95 transition-transform">
        {playing ? <Pause className="w-9 h-9" /> : <Play className="w-9 h-9" />} {playing ? "Pause" : label}
      </button>
    </>
  );
}

function SongList({ songs }) {
  return (
    <div className="flex flex-col gap-5" data-testid="family-songs-list">
      {songs.map((s) => (
        <div key={s.id} className="rounded-3xl bg-white border-4 border-black/5 p-6 flex flex-wrap items-center gap-5">
          <span className="flex-1 min-w-[12rem]">
            <span className="block font-patient font-black text-2xl text-gray-800">{s.song_title}</span>
            <span className="block font-patient text-xl text-gray-500">Chantée par {s.label}</span>
          </span>
          <BigPlay url={s.audio_url} testId={`patient-song-play-${s.id}`} />
        </div>
      ))}
    </div>
  );
}

function SongQuiz({ songs, onComplete }) {
  const order = useMemo(() => shuffle(songs), [songs]);
  const [i, setI] = useState(0);
  const [good, setGood] = useState(0);
  const [picked, setPicked] = useState(null);
  const start = useRef(Date.now());
  const song = order[i];
  const options = useMemo(() => shuffle([song, ...shuffle(songs.filter((s) => s.id !== song.id)).slice(0, 2)]), [song, songs]);

  const answer = (o) => {
    if (picked) return;
    document.querySelectorAll("audio").forEach((a) => a.pause());
    setPicked(o.id);
    const ok = o.id === song.id;
    const total = good + (ok ? 1 : 0);
    if (ok) setGood(total);
    setTimeout(() => {
      if (i + 1 >= order.length) {
        const score = Math.round((total / order.length) * 100);
        onComplete({ score, accuracy: score, duration: Math.round((Date.now() - start.current) / 1000) });
      } else { setI(i + 1); setPicked(null); }
    }, 1600);
  };

  return (
    <div className="text-center" data-testid="family-songs-quiz">
      <p className="font-patient text-xl text-gray-500 mb-2">Chanson {i + 1} sur {order.length}</p>
      <h2 className="font-serif-display italic text-3xl sm:text-4xl text-gray-800 mb-6">Quelle est cette chanson ?</h2>
      <BigPlay key={song.id} url={song.audio_url} testId="songs-quiz-play" />
      <div className="flex flex-col gap-4 mt-8 max-w-xl mx-auto">
        {options.map((o, k) => {
          const state = picked ? (o.id === song.id ? "ok" : o.id === picked ? "ko" : "") : "";
          return (
            <button key={o.id} onClick={() => answer(o)} data-testid={`songs-quiz-option-${k}`}
              className={`min-h-20 rounded-3xl border-4 px-6 font-patient font-black text-2xl transition-colors ${state === "ok" ? "bg-emerald-100 border-emerald-500 text-emerald-800" : state === "ko" ? "bg-amber-100 border-amber-300 text-gray-700" : "bg-white border-black/5 text-gray-800"}`}>
              {o.song_title} {state === "ok" && <Check className="inline w-7 h-7 ml-2" />}
            </button>
          );
        })}
      </div>
      {picked && <p className="font-patient text-xl text-gray-600 mt-6" data-testid="songs-quiz-reveal">Chantée par {song.label}</p>}
    </div>
  );
}

export default function FamilySongs({ songs, onComplete }) {
  const [mode, setMode] = useState("list");
  const canQuiz = songs.length >= 2;
  const tab = (k, Icon, label) => (
    <button onClick={() => setMode(k)} data-testid={`family-songs-mode-${k}`}
      className={`inline-flex items-center gap-2 h-16 px-6 rounded-full font-patient font-bold text-xl border-4 ${mode === k ? "bg-emerald-700 border-emerald-700 text-white" : "bg-white border-emerald-700/20 text-gray-700"}`}>
      <Icon className="w-6 h-6" /> {label}
    </button>
  );
  return (
    <div className="w-full max-w-2xl mx-auto soft-in" data-testid="family-songs">
      <h1 className="font-serif-display italic text-4xl text-gray-800 mb-6 text-center inline-flex items-center gap-3 w-full justify-center">
        <Music className="w-9 h-9 text-amber-600" /> Les chansons de la famille
      </h1>
      <GardienBadge id="gaston" eyebrow="Gaston vous présente les chansons de vos proches" className="mb-8" testId="family-songs-gaston" />
      {canQuiz && (
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          {tab("list", Headphones, "Écouter")}
          {tab("quiz", HelpCircle, "Deviner la chanson")}
        </div>
      )}
      {mode === "quiz" && canQuiz ? <SongQuiz songs={songs} onComplete={onComplete} /> : <SongList songs={songs} />}
    </div>
  );
}
